import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Dialog,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Filters
  const [city, setCity] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [openVideo, setOpenVideo] = useState(false);

  const handleShowVideo = (property) => {
    if (!property.videoId) {
      alert("No video available for this property");
      return;
    }
    const url = `https://skyline-properties-media.s3.ap-south-1.amazonaws.com/${property.videoId}`;
    setVideoUrl(url);
    setOpenVideo(true);
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  // Normalize MongoDB IDs
  const getPropertyId = (property) =>
    typeof property._id === "object" ? property._id?.$oid : property._id;

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}api/get-properties`);
      const data = await res.json();
      const items = data.data || [];
      setProperties(items);
      setFilteredProperties(items);
    } catch (err) {
      console.error("Error fetching properties:", err);
    } finally {
      setLoading(false);
    }
  };

  // Update status API
  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_URL}api/properties/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      setProperties((prev) =>
        prev.map((p) =>
          getPropertyId(p) === id ? { ...p, status: newStatus } : p
        )
      );
      setFilteredProperties((prev) =>
        prev.map((p) =>
          getPropertyId(p) === id ? { ...p, status: newStatus } : p
        )
      );
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  // Delete API
  const deleteProperty = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;
    try {
      await fetch(`${API_URL}api/properties/${id}`, {
        method: "DELETE",
      });

      setProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
      setFilteredProperties((prev) => prev.filter((p) => getPropertyId(p) !== id));
    } catch (err) {
      console.error("Failed to delete property:", err);
    }
  };

  // Filter handler
  const handleFilter = () => {
    let result = [...properties];

    if (city) {
      result = result.filter((p) =>
        p.city?.toLowerCase().includes(city.toLowerCase())
      );
    }

    if (priceRange) {
      const [min, max] = priceRange.split("-").map(Number);
      result = result.filter(
        (p) => Number(p.rate) >= min && Number(p.rate) <= max
      );
    }

    if (propertyType) {
      result = result.filter((p) =>
        p.propertyType?.toLowerCase().includes(propertyType.toLowerCase())
      );
    }

    setFilteredProperties(result);
  };

  return (
    <Box sx={{ p: 4, minHeight: "100vh", mt: 2 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        List Properties
      </Typography>

      {/* Filters */}
      <Paper
        elevation={6}
        sx={{
          p: 3,
          mb: 5,
          borderRadius: 4,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="City / Location"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained"
          onClick={handleFilter}
          sx={{
            px: 4,
            borderRadius: 3,
            bgcolor: "purple",
            fontWeight: "bold",
            color: "white",
          }}
        >
          Search
        </Button>
      </Paper>

      {/* Loader / No Data / Properties */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", minHeight: "50vh" }}>
          <CircularProgress size={70} sx={{ color: "purple" }} />
        </Box>
      ) : filteredProperties.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            minHeight: "40vh",
            flexDirection: "column",
            color: "gray",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            🚫 No Properties Found
          </Typography>
          <Typography>Try adjusting your filters or add a new property.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredProperties.map((property) => {
            const id = getPropertyId(property);
            const imageUrl = property.imageIds?.[0]
              ? `https://skyline-properties-media.s3.ap-south-1.amazonaws.com/${property.imageIds[0]}`
              : "/placeholder.jpg";

            const chips = [
              property.plotArea && `${property.plotArea} ${property.plotUnit}`,
              property.carpetArea && `Carpet: ${property.carpetArea}`,
              property.direction && `Dir: ${property.direction}`,
              property.bhkCount && `${property.bhkCount} ${property.bhkType}`,
            ].filter(Boolean);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                    overflow: "hidden",
                  }}
                >
                  <CardMedia component="img" height="200" image={imageUrl} alt={property.name || "Property"} />
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#6a1b9a" }}>
                      {property.rate
                        ? `₹${property.rate} ${property.priceUnit || ""}`
                        : property.rent
                          ? `Rent: ₹${property.rent}`
                          : "Price on Request"}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {property.name || "Unnamed Property"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {[property.location, property.city].filter(Boolean).join(", ") || "No Location"}
                    </Typography>

                    {/* Tags */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                      {chips.map((chip, i) => (
                        <Chip key={`${id}-chip-${i}`} label={chip} />
                      ))}
                    </Box>

                    {/* Status + Actions */}
                   {/* Status Selector */}
<FormControl fullWidth sx={{ mb: 2 }}>
  <InputLabel>Status</InputLabel>
  <Select
    value={property.status || "Available"}
    onChange={(e) => updateStatus(id, e.target.value)}
    size="small"
    sx={{ borderRadius: 2 }}
  >
    <MenuItem value="Available">Available</MenuItem>
    <MenuItem value="High Demand">High Demand</MenuItem>
    <MenuItem value="Sold">Sold</MenuItem>
  </Select>
</FormControl>

{/* Actions Row */}
<Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 1.5,
    mt: 1,
    justifyContent: "flex-start",
  }}
>
  {property.videoId && (
    <Button
      variant="contained"
      size="small"
      sx={{
        bgcolor: "#6a1b9a",
        color: "white",
        textTransform: "none",
        borderRadius: 3,
        px: 2,
        "&:hover": { bgcolor: "#4a148c" },
      }}
      onClick={() => handleShowVideo(property)}
    >
      Show Video
    </Button>
  )}

  <Button
    variant="outlined"
    size="small"
    sx={{
      textTransform: "none",
      borderRadius: 3,
      px: 2,
    }}
    onClick={() => navigate(`/edit/${id}`)}
  >
    Edit
  </Button>

  <IconButton
    size="small"
    color="error"
    sx={{
      bgcolor: "#ffebee",
      "&:hover": { bgcolor: "#ffcdd2" },
    }}
    onClick={() => deleteProperty(id)}
  >
    <DeleteIcon fontSize="small" />
  </IconButton>
</Box>

                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Video Dialog */}
      <Dialog
        open={openVideo}
        onClose={() => setOpenVideo(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          {videoUrl ? (
            <video
              src={videoUrl}
              controls
              autoPlay
              style={{ width: "100%", borderRadius: 8 }}
            />
          ) : (
            <Typography>No video available</Typography>
          )}
        </Box>
      </Dialog>

    </Box>
  );
};

export default PropertyList;
